import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSuiClient } from "@mysten/dapp-kit";
import type { SuiJsonRpcClient } from "@mysten/sui/jsonRpc";
import { readPolicyObject, type PolicyObject } from "@altheia-xyz/sui";
import { ChainBadge } from "@/components/ChainBadge";
import type { AgentObject } from "@/lib/api";

const coinDec = (t: string) => (/::usdc::usdc$|::deep::deep$/i.test(t) ? 6 : 9);
const coinSym = (t: string) => (/::sui::sui$/i.test(t) ? "SUI" : /::usdc::usdc$/i.test(t) ? "USDC" : /::deep::deep$/i.test(t) ? "DEEP" : (t.split("::").pop() ?? "?"));

/**
 * Sui fleet dashboard. Read-only display parity (DESIGN.md decision 2026-06-21):
 * agents are provisioned + revoked on-chain via @altheia-xyz/sui; this renders
 * the recorded ids as a ruled fleet table + a hairline stats strip.
 *
 * Persian + Orange: ghost ground, indigo structure, orange = live only,
 * coral = revoked. Mono data, serif-italic title. Ruled table (impeccable bans
 * identical card grids); stats are hairline cells, not boxed cards.
 */

type Status = "active" | "paused" | "revoked";

function statusOf(a: AgentObject, chainPol?: PolicyObject): Status {
  if (chainPol?.revoked || a.status === "revoked" || a.revoked_at) return "revoked";
  if (chainPol?.paused || a.status === "paused") return "paused";
  return "active";
}

interface Derived {
  capLabel: string;
  capDay: number;
  spent: number;
  asset: string;
  scope: string;
  expiry: string;
}

// Pull display fields from the policy object. The Move policy carries the live
// values; we surface the SUI per-day cap + the recorded scope/expiry for the row.
function derive(a: AgentObject, chainPol?: PolicyObject): Derived {
  if (chainPol && chainPol.caps.length > 0) {
    const c = chainPol.caps[0];
    const d = 10 ** coinDec(c.coinType);
    const sym = coinSym(c.coinType);
    const capDay = Number(c.perDayCap) / d;
    const spent = Number(c.spentToday) / d;
    const scope = chainPol.actions.some((id) => id >= 1 && id <= 3) ? "deepbook" : chainPol.actions.length ? "custody" : "default-deny";
    const expiry = Number(chainPol.expiresAtMs) > 0 ? new Date(Number(chainPol.expiresAtMs)).toISOString() : "";
    return { capLabel: capDay > 0 ? `${fmtNum(capDay)} ${sym}/day` : "no cap", capDay, spent, asset: sym, scope, expiry };
  }
  const pol = (a.policy ?? {}) as Record<string, unknown>;
  const caps = (pol.per_asset_caps ?? pol.caps) as
    | Array<{ asset?: string; per_tx?: number | string; per_day?: number | string; spent_today?: number | string }>
    | undefined;
  const actions = (pol.allowed_actions ?? pol.actions) as string[] | undefined;
  const primary = caps?.[0];
  const capDay = Number(primary?.per_day ?? 0);
  const asset = primary?.asset ?? "SUI";
  const spent = Number(primary?.spent_today ?? 0);
  const scope = scopeOf(actions);
  const expiry = (pol.expires_at as string | undefined) ?? "";
  return {
    capLabel: capDay > 0 ? `${capDay} ${asset}/day` : "no cap",
    capDay,
    spent,
    asset,
    scope,
    expiry,
  };
}

function scopeOf(actions?: string[]): string {
  if (!actions || actions.length === 0) return "default-deny";
  const protocols = new Set(actions.map((a) => a.split("_")[0]));
  return [...protocols].join(", ");
}

function fmtExpiry(iso: string): string {
  if (!iso) return "no expiry";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "no expiry";
  const days = Math.round((t - Date.now()) / 86_400_000);
  if (days < 0) return "expired";
  if (days === 0) return "expires today";
  return `${days}d left`;
}

function fmtNum(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function agentHref(a: AgentObject): string {
  return `/sui/agents/${a.id}`;
}

export function SuiDashboardPage({ agents }: { agents: AgentObject[] }) {
  const client = useSuiClient() as unknown as SuiJsonRpcClient;
  const [chainPols, setChainPols] = useState<Record<string, PolicyObject>>({});
  const polIds = agents.map((a) => a.sui_policy_id ?? "").join(",");
  useEffect(() => {
    let cancelled = false;
    const withPol = agents.filter((a) => a.sui_policy_id);
    Promise.allSettled(withPol.map((a) => readPolicyObject(client, a.sui_policy_id!).then((p) => [a.id, p] as const)))
      .then((rs) => {
        if (cancelled) return;
        const m: Record<string, PolicyObject> = {};
        for (const r of rs) if (r.status === "fulfilled") m[r.value[0]] = r.value[1];
        setChainPols(m);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polIds, client]);
  const rows = agents.map((a) => { const cp = chainPols[a.id]; return { agent: a, status: statusOf(a, cp), d: derive(a, cp) }; });
  const live = rows.filter((r) => r.status === "active").length;
  const dailyBudget = rows.reduce((s, r) => s + (r.status === "revoked" ? 0 : r.d.capDay), 0);
  const spentToday = rows.reduce((s, r) => s + (r.status === "revoked" ? 0 : r.d.spent), 0);
  const lastAudit = lastAuditLabel(agents);

  if (agents.length === 0) {
    return (
      <div className="fd fd--empty">
        <FleetHeader />
        <div className="fd__empty">
          <span className="fd__empty-kick">no agents on sui yet</span>
          <h2 className="fd__empty-title">The fleet is quiet.</h2>
          <p className="fd__empty-body">
            Provision a Sui agent through the SDK and it lands here with a capped
            vault, a scoped policy, and a revoke that settles on-chain.
          </p>
          <Link to="/sui/agents/new" className="fd__empty-cta">
            Create agent
            <Caret />
          </Link>
        </div>
        <FleetStyles />
      </div>
    );
  }

  return (
    <div className="fd">
      <FleetHeader />

      <section className="fd__stats" aria-label="fleet summary">
        <StatCell label="agents live" value={String(live)} accent />
        <StatCell label="daily budget" value={`${fmtNum(dailyBudget)} SUI`} />
        <StatCell label="spent today" value={`${fmtNum(spentToday)} SUI`} />
        <StatCell label="last audit" value={lastAudit} muted />
      </section>

      <div className="fd__tablewrap">
        <table className="fd__table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Status</th>
              <th className="r">Cap</th>
              <th>Spent today</th>
              <th>Scope</th>
              <th>Expiry</th>
              <th className="r"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ agent, status, d }) => {
              const pct = d.capDay > 0 ? Math.min(100, Math.round((d.spent / d.capDay) * 100)) : 0;
              const revoked = status === "revoked";
              return (
                <tr key={agent.id} className={revoked ? "is-revoked" : ""}>
                  <td>
                    <Link to={agentHref(agent)} className="fd__name">
                      {agent.name}
                    </Link>
                    <span className="fd__chain">
                      <ChainBadge chain="sui" />
                    </span>
                  </td>
                  <td>
                    <span className={`fd__status fd__status--${status}`}>
                      <span className="fd__dot" />
                      {status}
                    </span>
                  </td>
                  <td className="r fd__cap">{d.capLabel}</td>
                  <td>
                    <div className="fd__meter" aria-label={`${pct}% of daily cap spent`}>
                      <div className="fd__meter-track">
                        <div
                          className={`fd__meter-fill ${pct >= 90 ? "is-hot" : ""}`}
                          style={{ width: `${revoked ? 0 : pct}%` }}
                        />
                      </div>
                      <span className="fd__meter-num">
                        {revoked ? "—" : `${fmtNum(d.spent)} / ${fmtNum(d.capDay)}`}
                      </span>
                    </div>
                  </td>
                  <td className="fd__scope">{d.scope}</td>
                  <td className="fd__expiry">{revoked ? "revoked" : fmtExpiry(d.expiry)}</td>
                  <td className="r">
                    {revoked ? (
                      <span className="fd__revoked-mark">revoked</span>
                    ) : (
                      <Link to={agentHref(agent)} className="fd__revoke">
                        Revoke
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <FleetStyles />
    </div>
  );
}

function FleetHeader() {
  return (
    <header className="fd__head">
      <h1 className="fd__title">Fleet</h1>
      <p className="fd__sub">sui · on-chain agents under your operator key</p>
    </header>
  );
}

function StatCell({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: string;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="fd__stat">
      <span className="fd__stat-lbl">{label}</span>
      <span className={`fd__stat-val ${accent ? "is-accent" : ""} ${muted ? "is-muted" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function lastAuditLabel(agents: AgentObject[]): string {
  const times = agents
    .map((a) => a.revoked_at ?? a.created_at)
    .filter(Boolean)
    .map((t) => new Date(t as string).getTime())
    .filter((t) => !Number.isNaN(t));
  if (times.length === 0) return "none";
  const ms = Date.now() - Math.max(...times);
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function Caret() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" className="fd__caret">
      <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FleetStyles() {
  return (
    <style>{`
      .fd { max-width: var(--page-max); margin: 0 auto; font-family: var(--font-body); color: var(--bone); }
      .fd__head { margin-bottom: var(--s-6); }
      .fd__title { font: var(--type-display-page); font-style: italic; margin: 0; }
      .fd__sub { font: var(--type-mono-data); color: var(--slate-2); margin: var(--s-2) 0 0; letter-spacing: 0.02em; }

      /* Stats strip — hairline-separated cells, not boxed cards (impeccable). */
      .fd__stats { display: grid; grid-template-columns: repeat(4, 1fr); border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); margin-bottom: var(--s-7); }
      .fd__stat { display: flex; flex-direction: column; gap: var(--s-2); padding: var(--s-4) var(--s-5) var(--s-4) 0; border-left: 1px solid var(--line-soft); padding-left: var(--s-5); }
      .fd__stat:first-child { border-left: 0; padding-left: 0; }
      .fd__stat-lbl { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
      .fd__stat-val { font-family: var(--font-mono); font-variant-numeric: tabular-nums; font-size: 24px; line-height: 1; color: var(--bone); letter-spacing: -0.01em; }
      .fd__stat-val.is-accent { color: var(--accent-deep); }
      .fd__stat-val.is-muted { font-size: 18px; color: var(--slate); }

      /* Fleet table — ruled rows, no per-card elevation. */
      .fd__tablewrap { overflow-x: auto; }
      .fd__table { width: 100%; border-collapse: collapse; }
      .fd__table th { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate-3); text-align: left; font-weight: 400; padding: 0 var(--s-4) var(--s-3) 0; border-bottom: 1px solid var(--line); white-space: nowrap; }
      .fd__table th.r, .fd__table td.r { text-align: right; }
      .fd__table th:last-child, .fd__table td:last-child { padding-right: 0; }
      .fd__table td { font: var(--type-mono-data); padding: var(--s-4) var(--s-4) var(--s-4) 0; border-bottom: 1px solid var(--line-soft); vertical-align: middle; }
      .fd__table tbody tr { transition: background var(--dur-snap) var(--ease-snap); }
      .fd__table tbody tr:hover { background: var(--bg-elev); }
      .fd__table tbody tr.is-revoked td { color: var(--slate-3); }
      .fd__table tbody tr.is-revoked { background: var(--bg-revoked); }

      .fd__name { font: var(--type-body); color: var(--bone); font-weight: 500; }
      .fd__name:hover { color: var(--accent-deep); }
      .fd__chain { margin-left: var(--s-2); position: relative; top: -1px; }
      tr.is-revoked .fd__name { color: var(--slate-2); }

      .fd__status { display: inline-flex; align-items: center; gap: 7px; font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; }
      .fd__dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
      .fd__status--active { color: var(--ok); }
      .fd__status--active .fd__dot { background: var(--accent); animation: ds-pulse var(--dur-pulse) ease-in-out infinite; }
      .fd__status--paused { color: var(--slate); }
      .fd__status--paused .fd__dot { background: var(--slate); }
      .fd__status--revoked { color: var(--oxblood); }
      .fd__status--revoked .fd__dot { background: var(--oxblood); }

      .fd__cap { color: var(--bone-2); white-space: nowrap; }
      .fd__scope { color: var(--slate); text-transform: lowercase; }
      .fd__expiry { color: var(--slate); white-space: nowrap; }

      .fd__meter { display: flex; align-items: center; gap: var(--s-3); min-width: 168px; }
      .fd__meter-track { flex: 1; height: 3px; background: var(--line); position: relative; overflow: hidden; }
      .fd__meter-fill { height: 100%; background: var(--indigo); transition: width var(--dur-inscribe) var(--ease-inscribe); }
      .fd__meter-fill.is-hot { background: var(--accent-deep); }
      .fd__meter-num { font: var(--type-mono-address); color: var(--slate-2); white-space: nowrap; }

      .fd__revoke { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--oxblood); }
      .fd__revoke:hover { color: var(--oxblood-bright); text-decoration: underline; text-underline-offset: 3px; }
      .fd__revoked-mark { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate-3); }

      /* Empty state — centered in the content area, no dead top padding. */
      .fd--empty { min-height: 72vh; display: flex; flex-direction: column; }
      .fd--empty .fd__head { margin-bottom: 0; }
      .fd__empty { max-width: 520px; margin: auto 0; }
      .fd__empty-kick { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--accent-deep); }
      .fd__empty-title { font: var(--type-display-section); font-style: italic; font-size: 32px; margin: var(--s-3) 0 var(--s-3); }
      .fd__empty-body { font: var(--type-body-large); color: var(--slate); line-height: 1.6; margin: 0 0 var(--s-5); }
      .fd__empty-cta { display: inline-flex; align-items: center; gap: var(--s-2); font: var(--type-label); letter-spacing: var(--tracking-label); text-transform: uppercase; background: var(--indigo); color: var(--text-on-ink); padding: var(--s-3) var(--s-5); border-radius: var(--r-sm); transition: background var(--dur-snap) var(--ease-snap); }
      .fd__empty-cta:hover { background: var(--indigo-deep); }
      .fd__empty-cta:hover .fd__caret { transform: translateX(3px); }
      .fd__caret { transition: transform var(--dur-snap) var(--ease-snap); }

      @media (max-width: 768px) {
        .fd__stats { grid-template-columns: repeat(2, 1fr); }
        .fd__stat:nth-child(3) { border-left: 0; padding-left: 0; }
        .fd__stat { padding-top: var(--s-3); padding-bottom: var(--s-3); }
      }
      @media (prefers-reduced-motion: reduce) {
        .fd__dot, .fd__meter-fill { animation: none !important; transition: none !important; }
      }
    `}</style>
  );
}
